import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../App.css";
import { apiPostAuth } from "../../utils/api";
import toast from "react-hot-toast";

export default function LoanDetailPage() {
  const { loan_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [memberData, setMemberData] = useState(null);
  const [loanData, setLoanData] = useState(null);

  const [paymentModal, setPaymentModal] = useState(false);
  const [finishModal, setFinishModal] = useState(false);

  const [basePayment, setBasePayment] = useState(0);
  const [interestPayment, setInterestPayment] = useState(0);
  const [paymentDate, setPaymentDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchLoanDetail();
  }, [loan_id]);

  const fetchLoanDetail = async () => {
    try {
      const response = await apiPostAuth("loan/get_loan_detail.php", {
        loan_id,
      });

      if (response.result === 1) {
        setMemberData(response.member_data);
        setLoanData(response.loan_data);
      } else {
        navigate("/dashboard/member");
      }
    } catch (error) {
      navigate("/dashboard/member");
    } finally {
      setLoading(false);
    }
  };

  const loanAmount = Number(loanData?.loan_amount || 0);
  const interestRate = Number(loanData?.interest_rate || 0);
  const paymentTerm = Number(loanData?.payment_term || 0);
  const paidAmount = Number(loanData?.paid_amount || 0);
  const paidInterest = Number(loanData?.paid_interest || 0);

  const totalInterest = useMemo(() => {
    return loanAmount * (interestRate / 100) * paymentTerm;
  }, [loanAmount, interestRate, paymentTerm]);

  const totalPaidAmount = loanAmount + totalInterest;

  const baseProgress =
    loanAmount > 0 ? Math.min((paidAmount / loanAmount) * 100, 100) : 0;

  const interestProgress =
    totalInterest > 0 ? Math.min((paidInterest / totalInterest) * 100, 100) : 0;

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString.length < 14) return "-";

    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    const hour = dateString.slice(8, 10);
    const minute = dateString.slice(10, 12);
    const second = dateString.slice(12, 14);

    const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);

    return date.toLocaleString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openPaymentModal = () => {
    setBasePayment(0);
    setInterestPayment(0);
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setErrorMessage("");
    setPaymentModal(true);
  };

  const handleAddPayment = async () => {
    setErrorMessage("");

    const base = Number(basePayment || 0);
    const interest = Number(interestPayment || 0);

    if (base <= 0 && interest <= 0) {
      setErrorMessage("Salah satu harus diisi.");
      return;
    }

    if (!paymentDate) {
      setErrorMessage("Mohon pilih tanggal pembayaran.");
      return;
    }

    const toastId = toast.loading("Menambahkan Pembayaran...");

    try {
      const response = await apiPostAuth("loan/payment.php", {
        loan_id,
        base_payment: base,
        interest_payment: interest,
        payment_date: paymentDate,
      });

      if (response.result === 1) {
        toast.success("Pembayaran berhasil.", { id: toastId });
        setPaymentModal(false);
        fetchLoanDetail();
      } else {
        toast.error(response.message || "Gagal melakukan pembayaran", { id: toastId });
        setErrorMessage(response.message || "Gagal melakukan pembayaran.");
      }
    } catch (error) {
      toast.error("Gagal melakukan pembayaran.", { id: toastId });
      setErrorMessage("Gagal melakukan pembayaran.");
    }
  };

  const handleFinishLoan = async () => {
    const toastId = toast.loading("Menyelesaikan Pinjaman...");

    try {
      const response = await apiPostAuth("loan/finish_loan.php", {
        loan_id,
      });

      if (response.result === 1) {
        toast.success("Pinjaman telah diselesaikan.", { id: toastId });
        setFinishModal(false);
        fetchLoanDetail();
      } else {
        toast.error(response.message || "Gagal menyelesaikan pinjaman.", { id: toastId });
      }
    } catch (error) {
      toast.error("Gagal menyelesaikan pinjaman.", { id: toastId });
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    const confirmed = window.confirm(
        "Anda yakin ingin menghapus transaksi ini?"
    );

    if (!confirmed) {
        return;
    }

    const toastId = toast.loading(
        "Menghapus transaksi..."
    );

    try {
        const response = await apiPostAuth(
        "loan/delete_transaction.php",
        {
            transaction_id: transactionId,
        }
        );

        if (response.result === 1) {
        toast.success(
            "Transaksi telah dihapus.",
            {
            id: toastId,
            }
        );

        fetchLoanDetail();
        } else {
        toast.error(
            response.message ||
            "Gagal menghapus transaksi.",
            {
            id: toastId,
            }
        );
        }
    } catch (error) {
        toast.error(
        "Gagal menghapus transaksi.",
        {
            id: toastId,
        }
        );
    }
    };

  const handleOpenMemberDetail = (memberId) => {
    navigate(`/dashboard/member/${memberId}`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Memuat Detail Pinjaman...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate(-1)}
        style={backButtonStyle}
      >
        ← Kembali
      </button>

      <section style={heroSectionStyle}>
        <div>
          <h1 style={{ fontSize: "1.8rem", color: "#1f2937", margin: 0 }}>
            Detail Pinjaman
          </h1>

          <img
            src={memberData?.profile_picture}
            alt={memberData?.full_name}
            style={{
            width: "55px",
            height: "55px",
            borderRadius: "50%",
            objectFit: "cover",
            border: "1px solid #e5e7eb",
            }}
        />

          <p style={{ margin: "8px 0 0 0", color: "#6b7280" }}>
            Member: <strong>{memberData?.full_name}</strong>
          </p>

          <p style={{ margin: "5px 0 0 0", color: "#6b7280" }}>
            ID Member: {memberData?.member_id}
          </p>

          <button
            onClick={() => handleOpenMemberDetail(memberData?.member_id)}
            className="primary-button"
            style={{
                padding: "8px 16px",
                minWidth: "120px",
                margin: "5px 0 0 0"
            }}
            >
            Detail Member
            </button>
        </div>

        <div
          style={{
            ...statusBadgeStyle,
            backgroundColor: loanData?.is_active === 1 ? "#dcfce7" : "#fee2e2",
            color: loanData?.is_active === 1 ? "#166534" : "#696969",
          }}
        >
          {loanData?.is_active === 1 ? "Pinjaman Aktif" : "Pinjaman Selesai"}
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Proses Pembayaran</h2>

        <ProgressBar
          title="Pinjaman Pokok"
          current={paidAmount}
          total={loanAmount}
          percentage={baseProgress}
          formatCurrency={formatCurrency}
        />

        <ProgressBar
          title="Bunga"
          current={paidInterest}
          total={totalInterest}
          percentage={interestProgress}
          formatCurrency={formatCurrency}
        />
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Aksi</h2>

        <button
          onClick={openPaymentModal}
          className="primary-button"
          style={{ marginRight: "10px" }}
          disabled={loanData?.is_active !== 1}
        >
          Pembayaran
        </button>

        <button
          onClick={() => setFinishModal(true)}
          className="secondary-button"
          disabled={loanData?.can_finish !== 1}
        >
          Selesaikan Pinjaman
        </button>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Informasi Pinjaman</h2>

        <div style={infoGridStyle}>
          <InfoCard label="ID Pinjaman" value={loanData?.loan_id} />
          <InfoCard label="Pinjaman Pokok" value={formatCurrency(loanAmount)} />
          <InfoCard label="Bunga" value={`${interestRate}%`} />
          <InfoCard label="Durasi Pinjaman" value={`${paymentTerm} bulan`} />
          <InfoCard label="Estimasi Total Bunga" value={formatCurrency(totalInterest)} />
          <InfoCard label="Estimasi Pembayaran Pokok" value={formatCurrency(totalPaidAmount)} />
          <InfoCard label="Tanggal Pinjaman" value={formatDate(loanData?.loan_time_stamp)} />
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Riwayat Transaksi</h2>

        {!loanData?.transaction_history || loanData.transaction_history.length === 0 ? (
          <div style={emptyBoxStyle}>
            <p style={{ margin: 0, color: "#6b7280" }}>
              Belum ada transaksi untuk pinjaman ini
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "12px" }}>
            {loanData.transaction_history.map((transaction) => (
              <div key={transaction.transaction_id} style={transactionCardStyle}>
                <div>
                  <h3 style={{ margin: "0 0 5px 0", color: "#1f2937" }}>
                    {formatCurrency(transaction.transaction_amount)}
                  </h3>

                  <p style={metaTextStyle}>
                    Type:{" "}
                    <strong>
                      {transaction.transaction_type === "base"
                        ? "Cicilan/Pembayaran Pokok"
                        : "Pembayaran Bunga"}
                    </strong>
                  </p>

                  <p style={metaTextStyle}>
                    Tanggal: {transaction.transaction_time_stamp}
                  </p>
                </div>

                <div>
                    <button
                        onClick={() =>
                        handleDeleteTransaction(
                            transaction.transaction_id
                        )
                        }
                        style={{
                        border: "none",
                        backgroundColor: "#fee2e2",
                        color: "#dc2626",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        }}
                        disabled={loanData?.is_active === 0}
                    >
                        Hapus Transaksi
                </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {paymentModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0 }}>Pembayaran</h3>

            {errorMessage && <p style={errorTextStyle}>{errorMessage}</p>}

            <label style={labelStyle}>Cicilan/Pembayaran Pokok</label>
            <MoneyInput
              value={basePayment}
              onChange={(value) => setBasePayment(value)}
            />

            <label style={{ ...labelStyle, marginTop: "15px" }}>
              Bunga
            </label>
            <MoneyInput
              value={interestPayment}
              onChange={(value) => setInterestPayment(value)}
            />

            <label style={{ ...labelStyle, marginTop: "15px" }}>
              Tanggal Pembayaran
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              style={inputStyle}
            />

            <div style={{ marginTop: "20px" }}>
              <button
                onClick={handleAddPayment}
                className="primary-button"
                style={{ marginRight: "10px", width: "100px" }}
              >
                Simpan
              </button>

              <button
                onClick={() => {
                  setPaymentModal(false);
                  setErrorMessage("");
                }}
                className="secondary-button"
                style={{ width: "100px" }}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {finishModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ marginTop: 0 }}>Pinjaman Selesai?</h3>

            <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
              Anda yakin ingin menyelesaikan pinjaman ini? Pinjaman ini akan berubah menjadi selesai.
            </p>

            <div style={{ marginTop: "20px" }}>
              <button
                onClick={handleFinishLoan}
                className="primary-button"
                style={{ marginRight: "10px", width: "100px" }}
              >
                Iya
              </button>

              <button
                onClick={() => setFinishModal(false)}
                className="secondary-button"
                style={{ width: "100px" }}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div style={infoCardStyle}>
      <p style={infoLabelStyle}>{label}</p>
      <p style={infoValueStyle}>{value || "-"}</p>
    </div>
  );
}

function ProgressBar({ title, current, total, percentage, formatCurrency }) {
  return (
    <div style={{ marginBottom: "22px" }}>
      <div style={progressHeaderStyle}>
        <strong>{title}</strong>
        <span>{percentage.toFixed(1)}%</span>
      </div>

      <div style={progressOuterStyle}>
        <div
          style={{
            ...progressInnerStyle,
            width: `${percentage}%`,
          }}
        />
      </div>

      <p style={metaTextStyle}>
        {formatCurrency(current)} / {formatCurrency(total)}
      </p>
    </div>
  );
}

function MoneyInput({ value, onChange }) {
  return (
    <div style={moneyInputWrapperStyle}>
      <span style={moneyPrefixStyle}>Rp</span>
      <input
        type="number"
        onChange={(e) => onChange(e.target.value)}
        style={moneyInputStyle}
        placeholder="0"
      />
    </div>
  );
}

/* Styles */

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
};

const heroSectionStyle = {
  backgroundColor: "#fff",
  padding: "22px",
  borderRadius: "12px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: "20px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "18px",
  flexWrap: "wrap",
};

const sectionStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "1.2rem",
  marginBottom: "15px",
  color: "#1f2937",
};

const statusBadgeStyle = {
  display: "inline-block",
  padding: "8px 12px",
  borderRadius: "999px",
  fontSize: "0.85rem",
  fontWeight: "700",
};

const smallBadgeStyle = {
  padding: "6px 10px",
  borderRadius: "999px",
  fontSize: "0.8rem",
  fontWeight: "700",
  textTransform: "capitalize",
};

const infoGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "15px",
};

const infoCardStyle = {
  backgroundColor: "#f9fafb",
  padding: "15px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
};

const infoLabelStyle = {
  fontSize: "0.85rem",
  color: "#6b7280",
  margin: "0 0 6px 0",
};

const infoValueStyle = {
  fontSize: "1rem",
  fontWeight: "600",
  color: "#1f2937",
  margin: 0,
};

const progressHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  color: "#1f2937",
  marginBottom: "8px",
};

const progressOuterStyle = {
  width: "100%",
  height: "12px",
  backgroundColor: "#e5e7eb",
  borderRadius: "999px",
  overflow: "hidden",
};

const progressInnerStyle = {
  height: "100%",
  backgroundColor: "#2563eb",
  borderRadius: "999px",
  transition: "width 0.3s ease",
};

const transactionCardStyle = {
  backgroundColor: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "15px 18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
};

const metaTextStyle = {
  fontSize: "0.9rem",
  color: "#6b7280",
  margin: "5px 0 0 0",
};

const emptyBoxStyle = {
  padding: "20px",
  borderRadius: "10px",
  backgroundColor: "#f9fafb",
  border: "1px dashed #d1d5db",
  textAlign: "center",
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "20px",
};

const modalContentStyle = {
  backgroundColor: "#fff",
  padding: "22px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
  width: "380px",
  maxWidth: "95%",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  color: "#1f2937",
  fontWeight: "500",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "0.95rem",
};

const moneyInputWrapperStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#fff",
};

const moneyPrefixStyle = {
  padding: "10px 14px",
  backgroundColor: "#f9fafb",
  color: "#374151",
  fontWeight: "600",
  borderRight: "1px solid #d1d5db",
};

const moneyInputStyle = {
  flex: 1,
  padding: "10px",
  border: "none",
  outline: "none",
  fontSize: "0.95rem",
};

const errorTextStyle = {
  color: "#dc2626",
  fontSize: "0.9rem",
  marginBottom: "10px",
};